import { textBox } from './shapes.js';

/**
 * Fitting text inside a shape.
 *
 * A party box is 160 wide and holds "Marion Emma Cornell, as Life
 * Estate". Without wrapping, that runs out of both sides of the box and
 * over whatever is next to it — which on a dense chain is another
 * party's name, so the chart becomes unreadable exactly where it is
 * busiest.
 *
 * MEASURED, NOT GUESSED. Character counts are wrong by a factor that
 * varies with the string: "WWW" and "iii" are the same length and four
 * times the difference in width. A canvas 2D context measures the real
 * font, and the result is cached because a chain of four hundred boxes
 * re-measured on every render is the whole frame budget.
 *
 * The measurer is injected so this stays testable with no browser: the
 * wrapping decisions are the part that goes wrong, and they are pure
 * given a width function.
 */

/** A measurer backed by a canvas, cached per font. */
export function canvasMeasurer() {
  if (typeof document === 'undefined') return () => 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const cache = new Map();
  return (text, font) => {
    const key = `${font} ${text}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    ctx.font = font;
    const width = ctx.measureText(text).width;
    // Unbounded growth would be a leak on a long editing session; the
    // cap is generous enough that a whole chart stays cached.
    if (cache.size > 20000) cache.clear();
    cache.set(key, width);
    return width;
  };
}

export const fontOf = (shape) => (
  `${shape.italic ? 'italic ' : ''}${shape.bold ? '700' : '400'} ${shape.fontSize}px `
  + 'Inter, ui-sans-serif, system-ui, sans-serif'
);

/**
 * One paragraph, broken to fit a width.
 *
 * A word longer than the line is broken mid-word rather than allowed to
 * overhang: recording references like "SE/4SW/4,NE/4NW/4" have no spaces
 * in them and are exactly what these boxes are full of.
 */
function wrapParagraph(text, maxWidth, measure) {
  const words = String(text).split(/(\s+)/).filter((w) => w !== '');
  const lines = [];
  let line = '';

  const pushLine = () => { lines.push(line.replace(/\s+$/, '')); line = ''; };

  for (const word of words) {
    const candidate = line + word;
    if (measure(candidate) <= maxWidth || line === '') {
      // A single word that does not fit an empty line has to be cut.
      if (line === '' && measure(word) > maxWidth && !/^\s+$/.test(word)) {
        let chunk = '';
        for (const ch of word) {
          if (measure(chunk + ch) > maxWidth && chunk !== '') { lines.push(chunk); chunk = ch; } else chunk += ch;
        }
        line = chunk;
        continue;
      }
      line = candidate;
      continue;
    }
    pushLine();
    // Leading whitespace on a wrapped line is not text, it is a gap the
    // break already provided.
    line = /^\s+$/.test(word) ? '' : word;
  }
  if (line !== '') pushLine();
  return lines.length ? lines : [''];
}

// Text does not run to the border: at 2px stroke and 10pt type, letters
// touching the edge read as a box that is too small rather than as text
// that needs room.
export const PADDING = 6;

/**
 * Every line a shape's text becomes, in order.
 *
 * Explicit newlines are respected first and wrapped within — a party box
 * is a name over a date over a recording reference, and collapsing that
 * into one flowing paragraph loses the meaning of the layout.
 */
export function layoutText(shape, measure, padding = PADDING) {
  const raw = String(shape.text ?? '');
  if (!raw) return [];
  // The room the OUTLINE leaves, not the bounding box. A diamond 160
  // wide holds a box 80 wide at its middle, and wrapping to 160 puts
  // every line over the two sloping sides.
  const width = Math.max(4, textBox(shape, padding).w);
  const font = fontOf(shape);
  const measureIn = (text) => measure(text, font);
  // A text block grows to its content rather than clipping it, so it
  // does not wrap at all — that is what makes it an annotation.
  if (shape.shapeType === 'text' && shape.autoGrow !== false) {
    return raw.split('\n');
  }
  return raw.split('\n').flatMap((para) => (para === '' ? [''] : wrapParagraph(para, width, measureIn)));
}

/** Where the first baseline sits, given how many lines there are. */
export function firstBaseline(shape, lineCount, padding = PADDING) {
  const box = textBox(shape, padding);
  const lineHeight = shape.fontSize * 1.25;
  const block = lineHeight * lineCount;
  if (shape.valign === 'top') return box.y + shape.fontSize;
  if (shape.valign === 'bottom') return box.y + box.h - block + shape.fontSize;
  return box.y + (box.h - block) / 2 + shape.fontSize * 0.85;
}

/** Where a line starts across, for the shape's own alignment. */
export function textAnchorX(shape, padding = PADDING) {
  const box = textBox(shape, padding);
  if (shape.align === 'left') return box.x;
  if (shape.align === 'right') return box.x + box.w;
  return box.x + box.w / 2;
}
